import { motion } from 'framer-motion'
import { ChevronRight, ArrowRight, type LucideIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface PageHeaderProps {
  title: string
  subtitle?: string
  tag?: string
  icon?: LucideIcon
  gradient?: 'slate' | 'orange' | 'cyan' | 'purple' | 'emerald' | 'amber'
  showCta?: boolean
  ctaText?: string
  ctaAction?: () => void
  showScrollIndicator?: boolean
  pattern?: 'dots' | 'grid' | 'none'
}

const gradientStyles = {
  slate: 'from-slate-900 via-slate-800 to-slate-900',
  orange: 'from-slate-900 via-orange-950/80 to-slate-900',
  cyan: 'from-slate-900 via-cyan-950/80 to-slate-900',
  purple: 'from-slate-900 via-purple-950/80 to-slate-900',
  emerald: 'from-slate-900 via-emerald-950/80 to-slate-900',
  amber: 'from-slate-900 via-amber-950/80 to-slate-900',
}

const glowColors = {
  slate: ['bg-blue-500/20', 'bg-cyan-500/10'],
  orange: ['bg-[#f75e47]/20', 'bg-orange-500/10'],
  cyan: ['bg-cyan-500/20', 'bg-blue-500/10'],
  purple: ['bg-purple-500/20', 'bg-pink-500/10'],
  emerald: ['bg-emerald-500/20', 'bg-green-500/10'],
  amber: ['bg-amber-500/20', 'bg-yellow-500/10'],
}

const badgeColors = {
  slate: 'bg-white/10 text-cyan-400',
  orange: 'bg-[#f75e47]/20 text-[#f75e47]',
  cyan: 'bg-cyan-500/20 text-cyan-400',
  purple: 'bg-purple-500/20 text-purple-400',
  emerald: 'bg-emerald-500/20 text-emerald-400',
  amber: 'bg-amber-500/20 text-amber-400',
}

const ctaButtonColors = {
  slate: 'bg-[#f75e47] hover:bg-[#e04d36]',
  orange: 'bg-[#f75e47] hover:bg-[#e04d36]',
  cyan: 'bg-cyan-600 hover:bg-cyan-500',
  purple: 'bg-purple-600 hover:bg-purple-500',
  emerald: 'bg-emerald-600 hover:bg-emerald-500',
  amber: 'bg-amber-600 hover:bg-amber-500',
}

export function PageHeader({
  title,
  subtitle,
  tag,
  icon: Icon,
  gradient = 'slate',
  showCta = false,
  ctaText,
  ctaAction,
  showScrollIndicator = false,
  pattern = 'dots',
}: PageHeaderProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const glows = glowColors[gradient]
  const badgeClass = badgeColors[gradient]
  const ctaClass = ctaButtonColors[gradient]

  return (
    <section className={`relative min-h-[50vh] flex items-center bg-gradient-to-br ${gradientStyles[gradient]} overflow-hidden pt-20`}>
      {/* Background Pattern */}
      {pattern === 'dots' && (
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
      )}
      {pattern === 'grid' && (
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>
      )}

      {/* Decorative Elements */}
      <div className={`absolute top-1/4 right-1/4 w-96 h-96 ${glows[0]} rounded-full blur-3xl`} />
      <div className={`absolute bottom-1/4 left-1/4 w-64 h-64 ${glows[1]} rounded-full blur-3xl`} />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 lg:px-8 py-20 md:py-28">
        {/* Breadcrumb */}
        <nav className="mb-8" aria-label={t('aria.breadcrumb')}>
          <ol className="flex items-center justify-center space-x-2 text-sm">
            <li>
              <button
                onClick={() => navigate(`/${i18n.language}`)}
                className="text-white/70 hover:text-white transition-colors"
              >
                {t('nav.home')}
              </button>
            </li>
            <li className="flex items-center">
              <ChevronRight className="w-4 h-4 text-white/50 mx-2" />
              <span className="text-white">{title}</span>
            </li>
          </ol>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Tag with Icon */}
          {tag && Icon && (
            <span className={`inline-flex items-center gap-2 px-4 py-2 backdrop-blur-sm rounded-full text-sm font-medium mb-6 ${badgeClass}`}>
              <Icon className="w-4 h-4" />
              {tag}
            </span>
          )}

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}

          {/* CTA Buttons */}
          {showCta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              {ctaAction ? (
                <button
                  onClick={ctaAction}
                  className={`group inline-flex items-center gap-2 px-6 py-3 ${ctaClass} text-white font-semibold rounded-lg transition-all`}
                >
                  {ctaText || t('buttons.learnMore')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/${i18n.language}/contatti`)}
                  className={`group inline-flex items-center gap-2 px-6 py-3 ${ctaClass} text-white font-semibold rounded-lg transition-all`}
                >
                  {ctaText || t('buttons.contactUs')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Scroll Indicator */}
        {showScrollIndicator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-white/40"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  )
}
